/**
 * WCO-2322 — Unscanned-boxes warning for the supervisor's Daily Transfer Out view (the server-side submit
 * gate: finished boxes produced but not yet scanned to a destination). Data comes from
 * GET /daily-transfer-out/:date (Slice 5.7).
 *
 * NOTE: the per-destination "Output Destinations" reconciliation table was removed in the WCO-2322 UI
 * revision — the Production Summary header now lists the day's outputs + transfers directly.
 */
import React from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { AlertTriangle } from 'lucide-react';
import type { UnscannedBox } from '../../../services/dailyTransferOutService';

interface OutputReconciliationSectionProps {
  unscannedCount?: number;
  unscannedBoxes?: UnscannedBox[];
}

const OutputReconciliationSection: React.FC<OutputReconciliationSectionProps> = ({
  unscannedCount = 0,
  unscannedBoxes = [],
}) => {
  const { t } = useLanguage();

  if (unscannedCount === 0) return null;

  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
      <div className="text-xs text-amber-800 dark:text-amber-200">
        <span className="font-semibold">{unscannedCount}</span> {t('dailyTransferOut.unscannedBoxesWarning')}
        {unscannedBoxes.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-amber-700 dark:text-amber-300">
            {unscannedBoxes.slice(0, 20).map((b) => (
              <span key={b.barcode_number}>
                {b.item_number}{b.box_number ? ` #${b.box_number}` : ''}
              </span>
            ))}
            {unscannedBoxes.length > 20 && <span>+{unscannedBoxes.length - 20}…</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default OutputReconciliationSection;
